import { Component } from 'react';
import {
  Table,
  Input,
  Tag,
  message,
  Tooltip,
  Dropdown,
  notification,
  Progress,
} from 'antd';
import DealershipChooser from 'components/DealershipChooser';
import WorkOrderStore from 'stores/WorkOrderStore';
import { ShopStore } from 'stores';
import { PitstopButton } from 'shared';
import {
  PlusOutlined,
  DownOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import CurrentUserStore from 'stores/CurrentUserStore';
import AppStore from 'stores/App.js';
import debounce from 'lodash/debounce';
import { Modal, Button } from 'antd';
import { withRouter } from 'react-router-dom';
import {
  InfoCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SearchOutlined,
  LoadingOutlined,
  EditOutlined,
} from '@ant-design/icons';
import SFTPUploadDocModal from './modals/SFTPUploadDocModal';
import WorkOrderSelectionModal from './modals/WorkOrderSelectionModal';
import { webServiceProvider } from 'shared';
import ExternalIcon from 'assets/icons/external.svg?react';
import { EventBus } from 'stores/EventBus';
import { observe } from 'mobx';
import axios from 'axios';
import MediaPreview from 'components/General/MediaPreview';
import ThumbnailViewer from 'components/General/ThumbnailViewer';
import * as Sentry from '@sentry/react';

const formatDateTime = (date) => {
  const d = new Date(date);
  return `${d.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  })} ${d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })}`;
};

class SFTPWorkOrdersPage extends Component {
  state = {
    searchValue: '',
    data: [],
    editingKey: null, // ID of the row being edited
    editingValue: '', // Current editing value
    pagination: {
      current: 1,
      pageSize: 10,
    },
    loading: false,
    fileTransferFilters: [],
    workOrderAttachedFilter: [],
    isModalVisible: false,
    isTiff: false,
    base64data: null,
    previewImageUrl: '',
    tableFilters: {},
    previewImageUrls: [],
    currentPreviewIndex: 0,
    isSFTPUploadDocModalVisible: false,
    isErrorSFTPUpload: false,
    isWorkOrderSelectionModalVisible: false,
    currentRecordForAttachment: null,
    highlightedRowId: null, // Track which row is highlighted for attachment
  };

  constructor(props) {
    super(props);

    // Set up shop change watcher
    this.shopChangeWatcher = observe(ShopStore.currentShop, 'id', () => {
      if (ShopStore.currentShop.id !== -1) {
        this.fetchData(); // Refresh SFTP table when shop changes
      }
    });
  }

  renderImageWithFilename = (text, record) => {
    const { editingKey, editingValue } = this.state;
    const isEditing = editingKey === record.event_id;

    if (isEditing) {
      return (
        <Input
          value={editingValue}
          onChange={(e) => this.setState({ editingValue: e.target.value })}
          style={{ width: '100%' }}
          onPressEnter={this.saveEdit}
        />
      );
    }

    const httpUrl = record.file_url.replace(
      's3://work-orders-scan/',
      'https://work-orders-scan.s3.us-east-2.amazonaws.com/'
    );

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div
          onClick={() => this.showImagePreview(httpUrl)}
          style={{
            cursor: 'pointer',
            width: 35,
            height: 35,
            borderRadius: '5px',
            border: '1px solid #ccc',
            backgroundColor: '#f0f0f0',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ThumbnailViewer media={httpUrl} width={35} height={35} />
        </div>
        <div>{text}</div>
      </div>
    );
  };

  componentDidMount() {
    AppStore.setSelectedSidebarKey('/sftp-work-orders');
    this.fetchData();
    this.checkForPendingAttachments();

    EventBus.$on('sftp-work-orders/refresh', this.fetchData);

    // Listen for slide-out close events to clear highlighting
    EventBus.$on('dashboardWrapper/CLOSE_SLIDE_OUT', this.clearHighlighting);

    // Listen for work order save events to clear highlighting
    EventBus.$on('workOrders:saved', this.clearHighlighting);
  }

  componentWillUnmount() {
    EventBus.$off('sftp-work-orders/refresh', this.fetchData);
    EventBus.$off('dashboardWrapper/CLOSE_SLIDE_OUT', this.clearHighlighting);
    EventBus.$off('workOrders:saved', this.clearHighlighting);

    this.shopChangeWatcher();

    // Clean up any pending attachment listeners
    if (this.pendingAttachment) {
      EventBus.$off('workOrders:saved', this.handleWorkOrderSaved);
      this.pendingAttachment = null;
    }
  }

  // Clear row highlighting
  clearHighlighting = () => {
    this.setState({
      highlightedRowId: null,
    });
  };

  // Clear attachment state completely
  clearAttachmentState = () => {
    this.setState({
      currentRecordForAttachment: null,
      highlightedRowId: null,
    });
    localStorage.removeItem('sftp-attachment-state');
  };

  // Clean up pending attachment when slide-out is closed
  cleanupPendingAttachment = () => {
    if (this.pendingAttachment) {
      EventBus.$off('workOrders:saved', this.handleWorkOrderSaved);
      this.pendingAttachment = null;
      localStorage.removeItem('sftp-pending-attachment');
    }
    // Clear attachment state when slideout is closed
    this.clearAttachmentState();
  };

  // Check for pending attachments when component mounts
  checkForPendingAttachments = () => {
    const pendingAttachmentData = localStorage.getItem(
      'sftp-pending-attachment'
    );
    if (pendingAttachmentData) {
      try {
        const attachmentData = JSON.parse(pendingAttachmentData);
        this.pendingAttachment = attachmentData;
      } catch (error) {
        console.error('Error parsing pending attachment data:', error);
        localStorage.removeItem('sftp-pending-attachment');
      }
    }

    // Check for attachment state (highlighted row, etc.)
    const attachmentStateData = localStorage.getItem('sftp-attachment-state');
    if (attachmentStateData) {
      try {
        const attachmentState = JSON.parse(attachmentStateData);

        // Restore the highlighted row and attachment record
        this.setState({
          currentRecordForAttachment: attachmentState.record,
          highlightedRowId: attachmentState.highlightedRowId,
        });
      } catch (error) {
        console.error('Error parsing attachment state data:', error);
        localStorage.removeItem('sftp-attachment-state');
      }
    }
  };

  handleSearchChange = (e) => {
    const value = e.target.value;
    this.setState({ searchValue: value }); // Update the search input value in state
    this.fetchData(1, this.state.pagination.pageSize, value.trim()); // Trigger search with debounce
  };

  handleFileTransferFilterChange = (selectedKeys) => {
    this.setState({ fileTransferFilters: selectedKeys });
    this.fetchData(
      1,
      this.state.pagination.pageSize,
      this.state.searchValue.trim()
    );
  };

  handleWorkOrderAttachedFilterChange = (selectedKeys) => {
    this.setState({ workOrderAttachedFilter: selectedKeys });
    this.fetchData(
      1,
      this.state.pagination.pageSize,
      this.state.searchValue.trim()
    );
  };

  fetchData = debounce(async (page = 1, pageSize = 10, fileName = '') => {
    const { fileTransferFilters, workOrderAttachedFilter } = this.state;

    this.setState({ loading: true });

    try {
      const shopId = ShopStore.currentShop.id;
      const matched =
        workOrderAttachedFilter.length === 1
          ? workOrderAttachedFilter[0] === 'YES'
            ? true
            : false
          : undefined;

      const resp = await WorkOrderStore.getWorkOrderFileEvents({
        shopId,
        offset: (page - 1) * pageSize,
        limit: pageSize,
        ...(fileName && { fileName }),
        ...(fileTransferFilters.length > 0 && {
          status: fileTransferFilters.join(','),
        }),
        ...(matched !== undefined && { matched }),
      });

      const { data, meta } = resp;

      // ✅ Clear canvas refs to force re-render
      this.canvasRefs = {};

      this.setState({
        data,
        pagination: {
          current: page,
          pageSize,
          total: meta?.count || 0,
        },
        loading: false,
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      this.setState({ loading: false });
    }
  }, 300);

  handleTableChange = (pagination, filters, sorter) => {
    this.setState({ tableFilters: filters });

    const fileStatusFilters = filters.file_current_status || [];
    const workOrderFilters = filters.match_work_order_id || [];

    if (
      JSON.stringify(fileStatusFilters) !==
      JSON.stringify(this.state.fileTransferFilters)
    ) {
      this.setState({ fileTransferFilters: fileStatusFilters });
    }

    if (
      JSON.stringify(workOrderFilters) !==
      JSON.stringify(this.state.workOrderAttachedFilter)
    ) {
      this.setState({ workOrderAttachedFilter: workOrderFilters });
    }

    this.fetchData(
      pagination.current,
      pagination.pageSize,
      this.state.searchValue.trim()
    );

    this.setState({
      pagination: {
        ...pagination,
      },
    });
  };

  startEdit = (record) => {
    this.setState({
      editingKey: record.event_id,
      editingValue: record.work_order_id,
    });
  };

  saveEdit = async () => {
    const { data, editingKey, editingValue } = this.state;
    const shopId = ShopStore.currentShop.id; // Get the shop ID

    this.setState({ saving: true }); // Start loading

    try {
      const update = await WorkOrderStore.updateFileEvents({
        eventId: editingKey,
        workOrderId: editingValue,
        updatedBy: CurrentUserStore.user.email,
        shopId: shopId,
      });

      if (update && update.event_id) {
        message.success('Saved successfully!');

        const updatedData = data.map((item) =>
          item.event_id === editingKey
            ? {
                ...item,
                work_order_id: editingValue,
                invoice_status: update.invoice_status,
                match_work_order_id: update.match_work_order_id,
              }
            : item
        );
        this.setState({
          data: updatedData,
          editingKey: null,
          editingValue: '',
          saving: false, // Stop loading
        });
      } else {
        console.error(
          'Failed to update file events:',
          update || 'Unknown error'
        );
        message.error('Failed to save changes.');
        this.setState({ saving: false }); // Stop loading
      }
    } catch (error) {
      console.error('Error updating file events:', error);
      this.setState({ saving: false }); // Stop loading
    }
  };

  cancelEdit = () => {
    this.setState({ editingKey: null, editingValue: '' });
  };

  checkFileType = (base64) => {
    const firstBytes = base64.substring(0, 24);
    const binaryString = atob(firstBytes);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);

    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    let buffer = bytes.buffer;
    const view = new DataView(buffer);

    const byteOrder = view.getUint16(0, false);
    if (byteOrder === 0x4949 || byteOrder === 0x4d4d) return 'tiff';

    const pdfMagic = view.getUint32(0, false);
    if (pdfMagic === 0x25504446) return 'pdf';

    if (
      view.getUint8(0) === 0x89 &&
      view.getUint8(1) === 0x50 &&
      view.getUint8(2) === 0x4e &&
      view.getUint8(3) === 0x47
    ) {
      return 'png';
    }
    if (view.getUint8(0) === 0xff && view.getUint8(1) === 0xd8) {
      return 'jpeg';
    }
    return 'png';
  };

  showImagePreview = (url) => {
    const isPdf = url.includes('.pdf') || url.includes('application/pdf');
    // For PDFs, show the dedicated PDF handler
    this.setState({
      isModalVisible: true,
      base64data: url, // Pass URL instead of base64
      currentPreviewIndex: 0,
      isTiff: false,
      isPdf: isPdf, // Add flag to indicate it's a PDF
    });
  };

  showMultiPageTiffPreview = (url) => {
    // For TIFF files, pass URL directly
    this.setState({
      base64data: url, // Pass URL instead of base64
      isModalVisible: true,
      currentPreviewIndex: 0,
      isTiff: true,
    });
  };

  hideImagePreview = () => {
    this.setState({
      isModalVisible: false,
      previewImageUrl: '',
    });
  };

  forceUpdateTabs() {
    this.forceUpdate(); // Force a re-render of the component
  }

  openWorkOrderSlideOut = async (workOrderId) => {
    try {
      // Find the record that contains this work order ID to highlight the row
      // Try both work_order_id and match_work_order_id fields
      const record = this.state.data.find(
        (item) =>
          item.work_order_id === workOrderId ||
          item.match_work_order_id === workOrderId
      );
      if (record) {
        this.setState({
          highlightedRowId: record.event_id,
        });
      }

      // Fetch the work order data
      const workOrderData = await WorkOrderStore.getWorkOrderById(workOrderId);

      // Open the slide-out modal
      EventBus.$emit('dashboardWrapper/OPEN_SLIDE_OUT', {
        type: 'work-order',
        data: workOrderData,
        title: `Work Order #${workOrderData.invoice_number || workOrderData.id}`,
        selectedCarId: workOrderData.id_car,
        openedFromSFTP: true, // Add identifier to track SFTP origin
      });
    } catch (error) {
      console.error('Error fetching work order:', error);
      // Show error message instead of navigating to prevent page scroll
      Sentry.captureException(error, {
        tags: {
          component: 'SFTPWorkOrdersPage',
          method: 'openWorkOrderSlideOut',
        },
        extra: {
          workOrderId,
        },
      });
      message.error('Failed to load work order details.');
    }
  };

  createNewWorkOrder = async (record) => {
    // Set the highlighted row for new work order creation
    this.setState({
      highlightedRowId: record.event_id,
    });

    try {
      // Get the scanned data from the file event
      const scannedData = await WorkOrderStore.getWorkOrderByIdFromSFTP(
        record.event_id
      );

      // Note: New work order attachments are handled directly in WorkOrderDetails component
      // No need to store in localStorage for global service

      if (scannedData && scannedData.scanned) {
        // Parse the scanned data to extract work order information
        const parsedScannedData = JSON.parse(scannedData.scanned);

        // Open the slide-out with the extracted data
        EventBus.$emit('dashboardWrapper/OPEN_SLIDE_OUT', {
          type: 'work-order',
          data: {
            extractedData: parsedScannedData,
            scannedFile: {
              url: record.file_url,
              fileName: record.work_order_id,
              eventId: record.event_id,
            },
            isNew: true,
            hasScannedData: true,
          },
          title: 'New Work Order',
          isNew: true,
          openedFromSFTP: true, // Add identifier to track SFTP origin
        });
      } else {
        // Fallback if no scanned data available
        EventBus.$emit('dashboardWrapper/OPEN_SLIDE_OUT', {
          type: 'work-order',
          data: {
            isNew: true,
            hasScannedData: false,
          },
          title: 'New Work Order',
          isNew: true,
          openedFromSFTP: true, // Add identifier to track SFTP origin
        });
      }
    } catch (error) {
      console.error('Error fetching scanned data for new work order:', error);
      // Fallback to opening slide-out without data
      EventBus.$emit('dashboardWrapper/OPEN_SLIDE_OUT', {
        type: 'work-order',
        data: {
          isNew: true,
          hasScannedData: false,
        },
        title: 'New Work Order',
        isNew: true,
        openedFromSFTP: true, // Add identifier to track SFTP origin
      });
    }
  };

  attachExistingWorkOrder = (record) => {
    // Store attachment state in localStorage for persistence
    const attachmentState = {
      record: record,
      highlightedRowId: record.event_id,
      timestamp: Date.now(),
    };
    localStorage.setItem(
      'sftp-attachment-state',
      JSON.stringify(attachmentState)
    );

    this.setState({
      isWorkOrderSelectionModalVisible: true,
      currentRecordForAttachment: record,
      highlightedRowId: record.event_id, // Highlight the clicked row
    });
  };

  handleWorkOrderSelection = async (selectedWorkOrder) => {
    const { currentRecordForAttachment } = this.state;

    if (!currentRecordForAttachment || !selectedWorkOrder) {
      return;
    }

    try {
      // Get the scanned data from the file event
      const scannedData = await WorkOrderStore.getWorkOrderByIdFromSFTP(
        currentRecordForAttachment.event_id
      );

      // Use w_ord_id if available, otherwise fall back to invoice_number
      const workOrderId =
        selectedWorkOrder.w_ord_id || selectedWorkOrder.invoice_number;

      // Store the attachment info for later use (both in state and localStorage)
      const attachmentData = {
        eventId: currentRecordForAttachment.event_id,
        workOrderId: workOrderId,
        record: currentRecordForAttachment,
        selectedWorkOrder: selectedWorkOrder,
      };

      this.pendingAttachment = attachmentData;
      localStorage.setItem(
        'sftp-pending-attachment',
        JSON.stringify(attachmentData)
      );

      // Set up a one-time listener for when the work order is saved
      this.handleWorkOrderSaved = async (savedWorkOrder) => {
        if (this.pendingAttachment) {
          // Check multiple possible ID fields for matching
          const selectedId =
            this.pendingAttachment.selectedWorkOrder.id ||
            this.pendingAttachment.selectedWorkOrder.w_ord_id ||
            this.pendingAttachment.selectedWorkOrder.invoice_number;
          const savedId =
            savedWorkOrder.id ||
            savedWorkOrder.w_ord_id ||
            savedWorkOrder.invoice_number;
          if (selectedId === savedId) {
            // Remove the listener
            EventBus.$off('workOrders:saved', this.handleWorkOrderSaved);

            // Link the work order to the file
            await this.linkWorkOrderToFile(
              savedWorkOrder,
              this.pendingAttachment.record
            );

            // Clear the pending attachment
            this.pendingAttachment = null;
            localStorage.removeItem('sftp-pending-attachment');
            localStorage.removeItem('sftp-attachment-state');

            // Clear the attachment state
            this.clearAttachmentState();
          }
        }
      };

      // Listen for work order save events
      EventBus.$on('workOrders:saved', this.handleWorkOrderSaved);

      // Also listen for slide-out close to clean up
      EventBus.$on(
        'dashboardWrapper/CLOSE_SLIDE_OUT',
        this.cleanupPendingAttachment
      );

      // Open the slide-out with the selected work order data
      EventBus.$emit('dashboardWrapper/OPEN_SLIDE_OUT', {
        type: 'work-order',
        data: {
          ...selectedWorkOrder,
          extractedData:
            scannedData && scannedData.scanned
              ? JSON.parse(scannedData.scanned)
              : null,
          scannedFile: {
            url: currentRecordForAttachment.file_url,
            fileName: currentRecordForAttachment.work_order_id,
            eventId: currentRecordForAttachment.event_id,
          },
          isNew: false,
          hasScannedData: !!(scannedData && scannedData.scanned),
          pendingAttachment: {
            eventId: currentRecordForAttachment.event_id,
            workOrderId: workOrderId,
            record: currentRecordForAttachment,
          },
        },
        title: `Work Order #${selectedWorkOrder.invoice_number || selectedWorkOrder.id}`,
        selectedCarId: selectedWorkOrder.id_car,
        openedFromSFTP: true, // Add identifier to track SFTP origin
      });

      // Close the selection modal
      this.setState({
        isWorkOrderSelectionModalVisible: false,
      });
      // Don't clear currentRecordForAttachment here - it will be cleared when attachment completes
    } catch (error) {
      console.error('Error preparing work order for attachment:', error);
      message.error('Failed to prepare work order for attachment.');
    }
  };

  linkWorkOrderToFile = async (savedWorkOrder, fileRecord) => {
    this.setState({ loading: true });

    try {
      const shopId = ShopStore.currentShop.id;
      const workOrderId =
        savedWorkOrder.w_ord_id || savedWorkOrder.invoice_number;

      const update = await WorkOrderStore.updateFileEvents({
        eventId: fileRecord.event_id,
        workOrderId: workOrderId,
        updatedBy: CurrentUserStore.user.email,
        shopId: shopId,
      });

      if (update && update.event_id) {
        message.success('Work order attached successfully!');

        // Update the data with the new work order information
        const updatedData = this.state.data.map((item) =>
          item.event_id === fileRecord.event_id
            ? {
                ...item,
                work_order_id: workOrderId,
                invoice_status: update.invoice_status,
                match_work_order_id: update.match_work_order_id,
              }
            : item
        );

        this.setState({
          data: updatedData,
          loading: false,
        });
      } else {
        console.error(
          'Failed to update file events:',
          update || 'Unknown error'
        );
        message.error('Failed to attach work order.');
        this.setState({ loading: false });
      }
    } catch (error) {
      console.error('Error updating file events:', error);
      message.error('Failed to attach work order.');
      this.setState({ loading: false });
    }
  };

  handleSFTPUpload = async (files) => {
    // Close the modal
    this.setState({ isSFTPUploadDocModalVisible: false });

    AppStore.addLoading('Uploading files...');
    const fileUrls = [];
    const shopId = ShopStore.currentShop.id;

    try {
      // Filter out duplicate files
      const uniqueFiles = files
        .map((file) => file.originFileObj || file)
        .filter((file, index, array) => {
          const identifier = file.name + file.size;
          return (
            array.findIndex((item) => item.name + item.size === identifier) ===
            index
          );
        });

      // Upload each file to S3
      for (const file of uniqueFiles) {
        const fd = new FormData();
        fd.append('singleInputFileName', file);
        fd.append('bucketName', 'work-orders-scan');

        try {
          const data = await webServiceProvider.postFormData(
            `dashboard/v1/uploadimage?storageType=work-orders-scan&prefix=UFP-Workorder/staging/&location=SFTP&shopId=${shopId}&env=${import.meta.env.VITE_ENV}`,
            fd
          );
          if (data.fileUrl) {
            fileUrls.push(data.fileUrl);
          }
        } catch (e) {
          console.error('Error uploading file:', file.name, e);
          AppStore.addError(`Failed to upload ${file.name}`);
        }
      }

      if (fileUrls.length > 0) {
        // Show progress bar notification after upload completes
        const progressNotificationKey = 'upload-progress';
        let progress = 0;
        let notificationClosed = false;

        notification.open({
          key: progressNotificationKey,
          message: 'Processing files',
          description: (
            <div>
              <Progress
                percent={0}
                size="small"
                status="active"
                strokeColor="#1890ff"
              />
            </div>
          ),
          duration: 0, // Don't auto-close
          placement: 'topRight',
          icon: <LoadingOutlined style={{ color: '#1890ff' }} />,
          style: {
            width: 384,
          },
          onClose: () => {
            notificationClosed = true;
          },
        });

        // Start progress timer - fills up over 1 minute
        const progressInterval = setInterval(() => {
          // Check if notification was manually closed by user
          if (notificationClosed) {
            clearInterval(progressInterval);
            return;
          }
          const seconds = fileUrls?.length * 20 || 60;
          const interval = Math.round((100 / seconds) * 100) / 100;
          progress = Math.round(Math.min(progress + interval, 100) * 100) / 100; // 100% / 60 seconds = 1.67% per second, rounded to 2 decimal places

          // Update the notification with new progress
          notification.open({
            key: progressNotificationKey,
            message: 'Processing files',
            description: (
              <div>
                <Progress
                  percent={progress}
                  size="small"
                  status="active"
                  strokeColor="#1890ff"
                />
              </div>
            ),
            duration: 0,
            placement: 'topRight',
            icon: <LoadingOutlined style={{ color: '#1890ff' }} />,
            style: {
              width: 384,
            },
            onClose: () => {
              notificationClosed = true;
            },
          });

          // When progress reaches 100%, show scanning notification and refresh table
          if (progress >= 100) {
            clearInterval(progressInterval);

            // Close the progress notification
            notification.destroy(progressNotificationKey);

            // Show scanning notification
            notification.info({
              key: 'file-scanning-progress',
              message: 'File Scanning in Progress',
              description: (
                <div>
                  <div>
                    {fileUrls.length}{' '}
                    {fileUrls.length === 1 ? 'file is' : 'files are'} uploaded
                    and being scanned. Check back in {fileUrls.length * 2}{' '}
                    minutes for scan completion.
                  </div>
                </div>
              ),
              duration: 10, // Use seconds instead of milliseconds
              placement: 'topRight',
              icon: <ClockCircleOutlined style={{ color: '#1890ff' }} />,
              style: {
                width: 384,
              },
            });

            // Clear cache and reload table
            webServiceProvider.invalidateCachePattern(
              '/work-order/get-file-events'
            );
            EventBus.$emit('sftp-work-orders/refresh');
          }
        }, 1000);
      }
    } catch (error) {
      console.error('Error in batch upload:', error);
      AppStore.addError('Upload failed. Please try again.');
    } finally {
      AppStore.removeLoading('Uploading files...');
    }
  };

  render() {
    const {
      data,
      pagination,
      loading,
      editingKey,
      workOrderAttachedFilter,
      fileTransferFilters,
      isSFTPUploadDocModalVisible,
      isWorkOrderSelectionModalVisible,
    } = this.state;

    const columns = [
      {
        title: 'Filename',
        dataIndex: 'work_order_id',
        key: 'work_order_id',
        render: this.renderImageWithFilename,
      },

      {
        title: 'File Transfer',
        dataIndex: 'current_status',
        key: 'file_current_status',
        filters: [
          { text: 'In Progress', value: 'IN_PROGRESS' },
          { text: 'Complete', value: 'COMPLETED' },
          { text: 'Failed', value: 'FAILED' },
        ],
        filteredValue: fileTransferFilters,
        onFilter: () => true,
        render: (text) => {
          if (text === 'COMPLETED') {
            return <Tag color="green">Complete</Tag>;
          } else if (text === 'IN_PROGRESS') {
            return <Tag color="orange">In Progress</Tag>;
          }
          return (
            <Tooltip title="Unable to scan due to poor image quality or obstructions. Please manually enter details from the attached image.">
              <Tag color="red">
                <InfoCircleOutlined style={{ marginRight: 4 }} />
                Failed
              </Tag>
            </Tooltip>
          );
        },
      },
      {
        title: 'Work Order Attached',
        dataIndex: 'match_work_order_id',
        key: 'match_work_order_id',
        filters: [
          { text: 'No Work Order Attached', value: 'NO' },
          { text: 'Work Order Attached', value: 'YES' },
        ],
        filteredValue: workOrderAttachedFilter,
        onFilter: () => true,
        render: (text, record) =>
          text ? (
            <span
              onClick={() => this.openWorkOrderSlideOut(text)}
              style={{
                color: '#1677ff',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <span>#{record.work_order_id}</span>
              <ExternalIcon style={{ width: '12px', height: '12px' }} />
            </span>
          ) : (
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'new-work-order',
                    label: 'New Work Order',
                    onClick: () => this.createNewWorkOrder(record),
                  },
                  {
                    key: 'existing-work-order',
                    label: 'Existing Work Order',
                    onClick: () => this.attachExistingWorkOrder(record),
                  },
                ],
              }}
              trigger={['click']}
            >
              <span
                style={{
                  color: '#1677ff',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  border: '1px solid #1677FF',
                  borderRadius: '6px',
                  padding: '4px 8px',
                  backgroundColor: 'white',
                }}
              >
                <span>Attach Work Order</span>
                <DownOutlined style={{ fontSize: '12px' }} />
              </span>
            </Dropdown>
          ),
      },
      {
        title: 'Created At',
        dataIndex: 'created_at',
        key: 'created_at',
        render: (date) => <div>{formatDateTime(date)}</div>,
      },
      {
        title: 'Updated At',
        dataIndex: 'updated_at',
        key: 'updated_at',
        render: (date) => <div>{formatDateTime(date)}</div>,
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_, record) =>
          this.state.editingKey === record.event_id ? (
            <div style={{ display: 'flex', gap: '8px' }}>
              {this.state.saving ? (
                <LoadingOutlined style={{ fontSize: 18, color: 'blue' }} />
              ) : (
                <>
                  <CheckCircleOutlined
                    style={{ fontSize: 18, color: 'blue', cursor: 'pointer' }}
                    onClick={this.saveEdit}
                  />
                  <CloseCircleOutlined
                    style={{ fontSize: 18, color: 'blue', cursor: 'pointer' }}
                    onClick={this.cancelEdit}
                  />
                </>
              )}
            </div>
          ) : (
            <div>
              <EditOutlined
                style={{ fontSize: 18, color: 'blue', cursor: 'pointer' }}
                onClick={() => this.startEdit(record)}
              />

              <span
                onClick={() => this.startEdit(record)}
                style={{ marginLeft: 3, color: 'blue', cursor: 'pointer' }}
              >
                Edit
              </span>
            </div>
          ),
      },
    ];

    return (
      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <h1 style={{ fontWeight: 'bold' }}>Bulk Work Order Scan</h1>
          <PitstopButton
            icon={<PlusOutlined />}
            onClick={() => {
              this.setState({
                isSFTPUploadDocModalVisible: true,
                isErrorSFTPUpload: false,
              });
            }}
          >
            Add New
          </PitstopButton>
        </div>

        <SFTPUploadDocModal
          isVisible={isSFTPUploadDocModalVisible}
          onUpload={this.handleSFTPUpload}
          onCancel={() => {
            this.setState({ isSFTPUploadDocModalVisible: false });
          }}
        />
        <WorkOrderSelectionModal
          isVisible={isWorkOrderSelectionModalVisible}
          onCancel={() => {
            this.setState({
              isWorkOrderSelectionModalVisible: false,
              // Don't clear currentRecordForAttachment and highlightedRowId here
              // They will be cleared when attachment is completed or explicitly cancelled
            });
          }}
          onSelect={this.handleWorkOrderSelection}
          title="Select Work Order to Attach"
        />
        <DealershipChooser />
        <div
          style={{
            marginBottom: 16,
            display: 'flex',
            justifyContent: 'flex-start',
          }}
        >
          <Input
            placeholder="Search by Filename"
            value={this.state.searchValue}
            onChange={this.handleSearchChange}
            onPressEnter={() =>
              this.fetchData(
                1,
                pagination.pageSize,
                this.state.searchValue.trim()
              )
            }
            suffix={
              <SearchOutlined
                onClick={() =>
                  this.fetchData(
                    1,
                    pagination.pageSize,
                    this.state.searchValue.trim()
                  )
                }
                style={{ cursor: 'pointer' }}
              />
            }
            style={{ width: 300 }}
          />
        </div>

        <Table
          columns={columns}
          dataSource={data}
          pagination={pagination}
          loading={loading}
          onChange={this.handleTableChange}
          rowKey="id"
          onRow={(record) => ({
            style: {
              backgroundColor:
                this.state.highlightedRowId === record.event_id
                  ? '#f0f8ff'
                  : 'transparent',
            },
          })}
        />
        <Modal
          visible={this.state.isModalVisible}
          footer={null}
          onCancel={this.hideImagePreview}
          centered
          width={650}
          destroyOnClose
        >
          <MediaPreview media={this.state.base64data} />
        </Modal>
      </div>
    );
  }
}

export default withRouter(SFTPWorkOrdersPage);
